/**
 * ShareExport.ts
 * -----------------------------------------------------------------------------
 * The 3D model as drawn, written as one small glTF file for the phone / VR / AR
 * viewer (BridgeWeb/ShareViewer). Not the .bdbx: the phone has no engine, and
 * should not need one to look at a bridge.
 *
 * What goes in is what BridgeScene drew under its named model groups (see
 * MODEL_PARTS), with each vertex's colour as it is on screen. Overlays - the
 * selection, outlines, grid, labels, FE results - stay behind.
 *
 * How it gets small, in order of what it buys:
 *
 *   1. ONE DRAW CALL PER LOOK. Every part is baked to world space and poured
 *      into a handful of buckets (concrete, steel, unlit diagram, see-through,
 *      lines), colour riding per vertex. A phone draws five meshes instead of
 *      thousands, and the scene graph costs nothing.
 *   2. NO NORMALS, SHARED VERTICES. The model is drawn flat-shaded, so a normal
 *      per vertex is redundant: glTF says a primitive without normals is drawn
 *      flat, and three's GLTFLoader does that in the shader. Without normals,
 *      a corner shared by six faces of one colour is ONE vertex, not six.
 *   3. QUANTISED (KHR_mesh_quantization). Positions are 16-bit per axis over
 *      the model's box (75 m span: about 1 mm; 2 km: 3 cm), colours 8-bit.
 *   4. MESHOPT (EXT_meshopt_compression). Vertex and index streams through
 *      meshoptimizer's codecs, after reordering for the vertex cache.
 *   5. GZIP over the whole file. Meshopt output is built to be compressed
 *      again; the API stores bytes as given, so this is done here.
 *
 * The model is moved so its plan centre sits on the origin and its lowest
 * point on y = 0, which is where an AR placement puts the floor. The offset is
 * kept in asset.extras.origin. Metres, Y-up, as BridgeScene already is.
 */

import * as THREE from 'three';
import { MeshoptEncoder } from 'meshoptimizer/meshopt_encoder.module.js';

/** BridgeScene root children that ARE the model (BridgeScene.load names them). */
const MODEL_PARTS = new Set(['BridgeModel', 'Lanes', 'Ground', 'BridgeOutline', 'Reinforcement', 'Steel', 'Forces']);

/** Parts drawn with the steel response rather than matte concrete. */
const METAL_PARTS = new Set(['Steel', 'Reinforcement']);

/** Above this the file stops being something a phone should open. Reinforcement
 *  goes first, because it is most of a detailed model and least of its shape. */
const MAX_TRIANGLES = 4_000_000;

type Bucket = 'solid' | 'metal' | 'unlit' | 'glass' | 'lines';

const MATERIALS: Record<Bucket, Record<string, unknown>> = {
  solid: { name: 'Concrete', pbrMetallicRoughness: { baseColorFactor: [1, 1, 1, 1], metallicFactor: 0, roughnessFactor: 0.85 }, doubleSided: true },
  metal: { name: 'Steel', pbrMetallicRoughness: { baseColorFactor: [1, 1, 1, 1], metallicFactor: 0.45, roughnessFactor: 0.5 }, doubleSided: true },
  unlit: { name: 'Diagram', pbrMetallicRoughness: { baseColorFactor: [1, 1, 1, 1], metallicFactor: 0, roughnessFactor: 1 }, doubleSided: true,
           extensions: { KHR_materials_unlit: {} } },
  glass: { name: 'Transparent', pbrMetallicRoughness: { baseColorFactor: [1, 1, 1, 1], metallicFactor: 0, roughnessFactor: 0.85 }, doubleSided: true,
           alphaMode: 'BLEND' },
  lines: { name: 'Lines', pbrMetallicRoughness: { baseColorFactor: [1, 1, 1, 1], metallicFactor: 0, roughnessFactor: 1 },
           extensions: { KHR_materials_unlit: {} } },
};

export interface ShareExportStats {
  triangles: number;
  lines: number;
  vertices: number;
  /** The glTF before gzip, and the bytes handed back. */
  glbBytes: number;
  bytes: number;
  /** Set when something was left out (reinforcement over the budget). */
  notice: string;
  ms: number;
}

/** A drawable source: one mesh or line set, with what it costs. */
interface Source {
  obj: THREE.Mesh | THREE.LineSegments;
  part: string;
  lines: boolean;
  /** Primitives per instance (triangles or segments). */
  prims: number;
  instances: number;
}

export async function exportShareMesh(
  root: THREE.Object3D,
  camera: THREE.Camera,
  name: string,
): Promise<{ bytes: Uint8Array; stats: ShareExportStats }> {
  const t0 = performance.now();
  root.updateMatrixWorld(true);

  // ---- what is on screen ---------------------------------------------------
  let sources: Source[] = [];
  for (const part of root.children) {
    if (!MODEL_PARTS.has(part.name) || !part.visible) continue;
    part.traverseVisible((o) => {
      if (!o.layers.test(camera.layers)) return;
      const lines = (o as THREE.LineSegments).isLineSegments === true;
      const mesh = (o as THREE.Mesh).isMesh === true;
      if (!lines && !mesh) return;
      const obj = o as THREE.Mesh | THREE.LineSegments;
      const geom = obj.geometry as THREE.BufferGeometry;
      const pos = geom?.attributes?.position;
      if (!pos) return;
      const mat = Array.isArray(obj.material) ? obj.material[0] : obj.material;
      if (!mat || !mat.visible || (mat as THREE.MeshBasicMaterial).wireframe) return;
      const n = geom.index ? geom.index.count : pos.count;
      const prims = Math.floor(n / (lines ? 2 : 3));
      const instances = (obj as THREE.InstancedMesh).isInstancedMesh ? (obj as THREE.InstancedMesh).count : 1;
      if (prims > 0 && instances > 0) sources.push({ obj, part: part.name, lines, prims, instances });
    });
  }

  let notice = '';
  const triCount = (s: Source[]) => s.reduce((n, x) => n + (x.lines ? 0 : x.prims * x.instances), 0);
  if (triCount(sources) > MAX_TRIANGLES && sources.some((s) => s.part === 'Reinforcement')) {
    sources = sources.filter((s) => s.part !== 'Reinforcement');
    notice = 'Reinforcement was left out: with it the model is too large to open on a phone.';
  }
  const total = triCount(sources);
  if (total > MAX_TRIANGLES) {
    throw new Error(`The model has ${(total / 1e6).toFixed(1)} million triangles, more than a phone can open (${MAX_TRIANGLES / 1e6} million).`);
  }
  if (sources.length === 0) throw new Error('There is nothing in the 3D view to share.');

  // ---- pass 1: the world box ------------------------------------------------
  const box = new THREE.Box3();
  const v = new THREE.Vector3();
  const m = new THREE.Matrix4();
  for (const s of sources) {
    forEachInstance(s, m, (world) => {
      const pos = s.obj.geometry.attributes.position;
      for (let i = 0; i < pos.count; i++) box.expandByPoint(v.fromBufferAttribute(pos, i).applyMatrix4(world));
    });
  }
  const min = box.min.clone();
  const ext = box.getSize(new THREE.Vector3());
  const scale = [ext.x || 1, ext.y || 1, ext.z || 1].map((e) => e / 65535) as [number, number, number];
  const origin = new THREE.Vector3((box.min.x + box.max.x) / 2, box.min.y, (box.min.z + box.max.z) / 2);

  // ---- pass 2: quantise, colour, dedupe into buckets ------------------------
  const buckets = new Map<Bucket, VertexTable>();
  const table = (b: Bucket) => {
    let t = buckets.get(b);
    if (!t) buckets.set(b, (t = new VertexTable()));
    return t;
  };

  const q = new Uint16Array(9);
  const c = new Uint8Array(12);
  const idx = new Uint32Array(3);
  const rgba = [0, 0, 0, 0];

  for (const s of sources) {
    const geom = s.obj.geometry as THREE.BufferGeometry;
    const pos = geom.attributes.position;
    const index = geom.index;
    const per = s.lines ? 2 : 3;
    const colorOf = vertexColour(s.obj);
    const home: Bucket = s.lines ? 'lines' : s.part === 'Forces' ? 'unlit' : METAL_PARTS.has(s.part) ? 'metal' : 'solid';

    forEachInstance(s, m, (world, instance) => {
      for (let p = 0; p < s.prims; p++) {
        let seeThrough = false;
        for (let k = 0; k < per; k++) {
          const i = index ? index.getX(p * per + k) : p * per + k;
          v.fromBufferAttribute(pos, i).applyMatrix4(world);
          q[k * 3] = quant(v.x, min.x, scale[0]);
          q[k * 3 + 1] = quant(v.y, min.y, scale[1]);
          q[k * 3 + 2] = quant(v.z, min.z, scale[2]);
          colorOf(i, instance, rgba);
          c[k * 4] = rgba[0]; c[k * 4 + 1] = rgba[1]; c[k * 4 + 2] = rgba[2]; c[k * 4 + 3] = rgba[3];
          if (rgba[3] < 255) seeThrough = true;
        }
        const t = table(!s.lines && seeThrough ? 'glass' : home);
        for (let k = 0; k < per; k++) idx[k] = t.vertex(q, k * 3, c, k * 4);
        // Collapsed by quantisation (or degenerate to begin with): nothing to draw.
        if (idx[0] === idx[1] || (per === 3 && (idx[1] === idx[2] || idx[0] === idx[2]))) continue;
        for (let k = 0; k < per; k++) t.index(idx[k]);
      }
    });
  }

  // ---- encode ------------------------------------------------------------------
  await MeshoptEncoder.ready;
  const glb = writeGlb(buckets, {
    name,
    translation: [min.x - origin.x, min.y - origin.y, min.z - origin.z],
    scale,
    origin: [origin.x, origin.y, origin.z],
  });
  const bytes = await gzip(glb.bytes);

  const stats: ShareExportStats = {
    ...glb.stats,
    glbBytes: glb.bytes.length,
    bytes: bytes.length,
    notice,
    ms: Math.round(performance.now() - t0),
  };
  console.info(`[share] ${stats.triangles} triangles, ${stats.lines} lines, ${stats.vertices} vertices -> `
    + `${(stats.glbBytes / 1024).toFixed(0)} KB glb, ${(stats.bytes / 1024).toFixed(0)} KB gz in ${stats.ms} ms`);
  return { bytes, stats };
}

// =============================================================================
//  Reading the scene
// =============================================================================

function forEachInstance(s: Source, m: THREE.Matrix4, fn: (world: THREE.Matrix4, instance: number) => void): void {
  const obj = s.obj;
  if ((obj as THREE.InstancedMesh).isInstancedMesh) {
    const im = obj as THREE.InstancedMesh;
    for (let i = 0; i < s.instances; i++) {
      im.getMatrixAt(i, m);
      m.premultiply(obj.matrixWorld);
      fn(m, i);
    }
  } else {
    fn(obj.matrixWorld, 0);
  }
}

function quant(x: number, min: number, step: number): number {
  const n = Math.round((x - min) / step);
  return n < 0 ? 0 : n > 65535 ? 65535 : n;
}

/**
 * The colour a vertex is drawn in, as linear RGBA bytes: material colour, times
 * its texture's average (Texture mode), times vertex and instance colour.
 * Material opacity is NOT folded in: the view's Transparency slider fades every
 * material, and a shared model should arrive solid. A part's own see-through
 * colour comes in as vertex alpha and is kept.
 */
function vertexColour(obj: THREE.Mesh | THREE.LineSegments): (i: number, instance: number, out: number[]) => void {
  const mat = (Array.isArray(obj.material) ? obj.material[0] : obj.material) as THREE.MeshStandardMaterial;
  const base = new THREE.Color(1, 1, 1);
  if (mat.color) base.copy(mat.color);
  if (mat.map) base.multiply(textureAverage(mat.map));

  const geom = obj.geometry as THREE.BufferGeometry;
  const col = mat.vertexColors ? geom.attributes.color : undefined;
  const inst = (obj as THREE.InstancedMesh).isInstancedMesh ? (obj as THREE.InstancedMesh).instanceColor : null;
  const tmp = new THREE.Color();

  return (i, instance, out) => {
    let r = base.r, g = base.g, b = base.b, a = 1;
    if (col) {
      r *= col.getX(i); g *= col.getY(i); b *= col.getZ(i);
      if (col.itemSize === 4) a = col.getW(i);
    }
    if (inst) {
      tmp.fromBufferAttribute(inst, instance);
      r *= tmp.r; g *= tmp.g; b *= tmp.b;
    }
    out[0] = byte(r); out[1] = byte(g); out[2] = byte(b); out[3] = byte(a);
  };
}

function byte(x: number): number {
  const n = Math.round(x * 255);
  return n < 0 ? 0 : n > 255 ? 255 : n;
}

const textureAverages = new Map<string, THREE.Color>();

/** A texture's mean colour (linear), for Texture mode: the phone gets the
 *  asphalt's grey, not the asphalt. */
function textureAverage(tex: THREE.Texture): THREE.Color {
  const hit = textureAverages.get(tex.uuid);
  if (hit) return hit;
  const out = new THREE.Color(0.6, 0.6, 0.6);
  try {
    const img = tex.image as CanvasImageSource | undefined;
    if (img) {
      const cv = document.createElement('canvas');
      cv.width = cv.height = 16;
      const ctx = cv.getContext('2d', { willReadFrequently: true });
      if (ctx) {
        ctx.drawImage(img, 0, 0, 16, 16);
        const d = ctx.getImageData(0, 0, 16, 16).data;
        let r = 0, g = 0, b = 0;
        for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; }
        const n = (d.length / 4) * 255;
        out.setRGB(r / n, g / n, b / n, THREE.SRGBColorSpace);
      }
    }
  } catch {
    /* tainted or not drawable: keep the neutral grey */
  }
  textureAverages.set(tex.uuid, out);
  return out;
}

// =============================================================================
//  Vertex dedupe
// =============================================================================

/** Unique (quantised position, colour) vertices and the indices into them. */
class VertexTable {
  /** x, y, z, 0 per vertex: the 4th lane pads the stride to 8 bytes, which the
   *  meshopt vertex codec requires (a multiple of 4). */
  pos = new Uint16Array(4 * 1024);
  col = new Uint8Array(4 * 1024);
  count = 0;
  indices = new Uint32Array(3 * 1024);
  indexCount = 0;
  private slots = new Int32Array(4096).fill(-1);

  vertex(q: Uint16Array, qi: number, c: Uint8Array, ci: number): number {
    const x = q[qi], y = q[qi + 1], z = q[qi + 2];
    const k = (c[ci] | (c[ci + 1] << 8) | (c[ci + 2] << 16) | (c[ci + 3] << 24)) >>> 0;
    const mask = this.slots.length - 1;
    let h = (Math.imul(x, 73856093) ^ Math.imul(y, 19349663) ^ Math.imul(z, 83492791) ^ Math.imul(k, 2654435761)) & mask;
    for (;;) {
      const s = this.slots[h];
      if (s < 0) break;
      const p = s * 4;
      if (this.pos[p] === x && this.pos[p + 1] === y && this.pos[p + 2] === z &&
          ((this.col[p] | (this.col[p + 1] << 8) | (this.col[p + 2] << 16) | (this.col[p + 3] << 24)) >>> 0) === k) return s;
      h = (h + 1) & mask;
    }

    const id = this.count++;
    if (id * 4 + 4 > this.pos.length) {
      const pos = new Uint16Array(this.pos.length * 2); pos.set(this.pos); this.pos = pos;
      const col = new Uint8Array(this.col.length * 2); col.set(this.col); this.col = col;
    }
    const p = id * 4;
    this.pos[p] = x; this.pos[p + 1] = y; this.pos[p + 2] = z;
    this.col[p] = c[ci]; this.col[p + 1] = c[ci + 1]; this.col[p + 2] = c[ci + 2]; this.col[p + 3] = c[ci + 3];
    this.slots[h] = id;
    if (this.count * 2 > this.slots.length) this.rehash();
    return id;
  }

  index(i: number): void {
    if (this.indexCount === this.indices.length) {
      const grown = new Uint32Array(this.indices.length * 2);
      grown.set(this.indices);
      this.indices = grown;
    }
    this.indices[this.indexCount++] = i;
  }

  private rehash(): void {
    const slots = new Int32Array(this.slots.length * 2).fill(-1);
    const mask = slots.length - 1;
    for (let id = 0; id < this.count; id++) {
      const p = id * 4;
      const k = (this.col[p] | (this.col[p + 1] << 8) | (this.col[p + 2] << 16) | (this.col[p + 3] << 24)) >>> 0;
      let h = (Math.imul(this.pos[p], 73856093) ^ Math.imul(this.pos[p + 1], 19349663) ^
               Math.imul(this.pos[p + 2], 83492791) ^ Math.imul(k, 2654435761)) & mask;
      while (slots[h] >= 0) h = (h + 1) & mask;
      slots[h] = id;
    }
    this.slots = slots;
  }
}

// =============================================================================
//  The glTF
// =============================================================================

interface Placement {
  name: string;
  translation: number[];
  scale: number[];
  origin: number[];
}

function writeGlb(buckets: Map<Bucket, VertexTable>, at: Placement): {
  bytes: Uint8Array;
  stats: { triangles: number; lines: number; vertices: number };
} {
  const chunks: Uint8Array[] = [];
  let binLength = 0;
  let fallbackLength = 0;
  const bufferViews: unknown[] = [];
  const accessors: unknown[] = [];
  const meshes: unknown[] = [];
  const nodes: unknown[] = [];
  const materials: unknown[] = [];
  const stats = { triangles: 0, lines: 0, vertices: 0 };

  const addView = (raw: Uint8Array, count: number, stride: number, mode: 'ATTRIBUTES' | 'TRIANGLES' | 'INDICES', target: number) => {
    const enc = MeshoptEncoder.encodeGltfBuffer(raw, count, stride, mode);
    const pad = (4 - (binLength % 4)) % 4;
    if (pad) { chunks.push(new Uint8Array(pad)); binLength += pad; }
    const ext = { buffer: 0, byteOffset: binLength, byteLength: enc.length, byteStride: stride, mode, count };
    chunks.push(enc);
    binLength += enc.length;

    const view: Record<string, unknown> = {
      buffer: 1, byteOffset: fallbackLength, byteLength: raw.length, target,
      extensions: { EXT_meshopt_compression: ext },
    };
    if (mode === 'ATTRIBUTES') view.byteStride = stride;
    fallbackLength += (raw.length + 3) & ~3;
    bufferViews.push(view);
    return bufferViews.length - 1;
  };

  // Lines last, so they draw over the faces they outline.
  const order: Bucket[] = ['solid', 'metal', 'unlit', 'glass', 'lines'];
  for (const kind of order) {
    const t = buckets.get(kind);
    if (!t || t.indexCount === 0) continue;
    const lines = kind === 'lines';

    // Vertex-cache order, then vertex-fetch order; this is also what makes the
    // index codec effective. Unused vertices (their triangles collapsed) drop out.
    const indices = t.indices.slice(0, t.indexCount);
    const [remap, unique] = MeshoptEncoder.reorderMesh(indices, !lines, !lines);
    const pos = new Uint16Array(unique * 4);
    const col = new Uint8Array(unique * 4);
    const lo = [65535, 65535, 65535], hi = [0, 0, 0];
    for (let i = 0; i < t.count; i++) {
      const r = remap[i];
      if (r === 0xffffffff) continue;
      for (let a = 0; a < 4; a++) { pos[r * 4 + a] = t.pos[i * 4 + a]; col[r * 4 + a] = t.col[i * 4 + a]; }
      for (let a = 0; a < 3; a++) {
        const x = t.pos[i * 4 + a];
        if (x < lo[a]) lo[a] = x;
        if (x > hi[a]) hi[a] = x;
      }
    }

    const wide = unique > 65535;
    const idxBytes = wide
      ? new Uint8Array(indices.buffer, 0, indices.length * 4)
      : new Uint8Array(Uint16Array.from(indices).buffer);

    const posView = addView(new Uint8Array(pos.buffer), unique, 8, 'ATTRIBUTES', 34962);
    const colView = addView(col, unique, 4, 'ATTRIBUTES', 34962);
    const idxView = addView(idxBytes, indices.length, wide ? 4 : 2, lines ? 'INDICES' : 'TRIANGLES', 34963);

    accessors.push({ bufferView: posView, componentType: 5123, count: unique, type: 'VEC3', min: lo, max: hi });
    accessors.push({ bufferView: colView, componentType: 5121, normalized: true, count: unique, type: 'VEC4' });
    accessors.push({ bufferView: idxView, componentType: wide ? 5125 : 5123, count: indices.length, type: 'SCALAR' });
    const a = accessors.length - 3;

    materials.push(MATERIALS[kind]);
    meshes.push({
      name: kind,
      primitives: [{
        attributes: { POSITION: a, COLOR_0: a + 1 },
        indices: a + 2,
        material: materials.length - 1,
        mode: lines ? 1 : 4,
      }],
    });
    nodes.push({ name: kind, mesh: meshes.length - 1, translation: at.translation, scale: at.scale });

    stats.vertices += unique;
    if (lines) stats.lines += indices.length / 2; else stats.triangles += indices.length / 3;
  }

  if (meshes.length === 0) throw new Error('There is nothing in the 3D view to share.');

  const used = ['KHR_mesh_quantization', 'EXT_meshopt_compression'];
  if (buckets.get('unlit')?.indexCount || buckets.get('lines')?.indexCount) used.push('KHR_materials_unlit');

  const json = {
    asset: {
      version: '2.0',
      generator: 'Bridge Express share export',
      // Read by the share viewer: the model's name, and where its origin was.
      extras: { name: at.name, units: 'm', upAxis: 'Y', origin: at.origin, format: 1 },
    },
    extensionsUsed: used,
    extensionsRequired: ['KHR_mesh_quantization', 'EXT_meshopt_compression'],
    scene: 0,
    scenes: [{ name: at.name, nodes: nodes.map((_, i) => i) }],
    nodes,
    meshes,
    materials,
    accessors,
    bufferViews,
    buffers: [
      { byteLength: binLength },
      // The decompressed size, with no data: EXT_meshopt_compression's
      // "fallback" buffer, which a loader that decodes never reads.
      { byteLength: fallbackLength, extensions: { EXT_meshopt_compression: { fallback: true } } },
    ],
  };

  // GLB: 12-byte header, JSON chunk (space-padded), BIN chunk (zero-padded).
  const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
  const jsonLen = (jsonBytes.length + 3) & ~3;
  const binLen = (binLength + 3) & ~3;
  const out = new Uint8Array(12 + 8 + jsonLen + 8 + binLen);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, 0x46546c67, true);           // 'glTF'
  dv.setUint32(4, 2, true);
  dv.setUint32(8, out.length, true);
  dv.setUint32(12, jsonLen, true);
  dv.setUint32(16, 0x4e4f534a, true);          // 'JSON'
  out.set(jsonBytes, 20);
  out.fill(0x20, 20 + jsonBytes.length, 20 + jsonLen);
  let o = 20 + jsonLen;
  dv.setUint32(o, binLen, true);
  dv.setUint32(o + 4, 0x004e4942, true);       // 'BIN\0'
  o += 8;
  for (const ch of chunks) { out.set(ch, o); o += ch.length; }

  return { bytes: out, stats };
}

async function gzip(bytes: Uint8Array): Promise<Uint8Array> {
  if (typeof CompressionStream === 'undefined') return bytes;   // the viewer reads either
  const stream = new Blob([bytes as Uint8Array<ArrayBuffer>]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

